import { createColumnHelper } from '@atbay/cyber-components'
import Show from '@/ui/components/helpers/Show'
import Typography from '@/ui/components/typography'
import { StyledCustomTable } from '@/ui/components/CustomTable/CustomTable.styles'
import TableEmpty from '@/ui/components/table/table-empty'
import { useAssetsTable } from '@routes/digital-footprint/routes/assets/components/assetsTable/useAssetsTable/useAssetsTable'
import {
    Cell,
    SeverityCell,
    IssuesCell,
    IssueCountItem,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/assetsTable.styles'
import {
    getHighestSeverity,
    countIssuesBySeverity,
    SEVERITY_TEXT_MAPPING,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'

interface DomainsTableProps {
    assets: TAsset[]
    emptyAssetsMessage: string
}

const DOMAIN_CLASSIFICATION_LABELS: Partial<Record<TAssetType, string>> = {
    domain: 'Root Domain',
    subdomain: 'Subdomain',
    email: 'Email Domain',
}

const columnHelper = createColumnHelper<TAsset>()

const DomainsTable = ({ assets, emptyAssetsMessage }: DomainsTableProps) => {
    const { sorting, setSorting, handleRowClick, activeAssetId } =
        useAssetsTable({ assets, origin: 'domains_table' })

    const columns = [
        {
            id: 'risk',
            ...columnHelper.accessor('issues', {
                cell: (info) => {
                    const asset = info.row.original
                    const highestSeverity = getHighestSeverity(asset.issues)

                    return (
                        <SeverityCell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid={`domains-table-risk-${highestSeverity}`}
                        >
                            <SeverityIcon severity={highestSeverity} />
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                testId="risk"
                            >
                                {SEVERITY_TEXT_MAPPING[highestSeverity]}
                            </Typography>
                        </SeverityCell>
                    )
                },
                header: () => (
                    <Typography variant="s-regular">Severity</Typography>
                ),
                enableSorting: true,
            }),
            width: '20%',
        },
        {
            ...columnHelper.accessor('hostname', {
                cell: (info) => {
                    return (
                        <Cell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid="domains-table-domain-name"
                        >
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                testId="domain-name"
                            >
                                {info.row.original.hostname}
                            </Typography>
                        </Cell>
                    )
                },
                header: () => (
                    <Typography variant="s-regular">Domain Name</Typography>
                ),
                enableSorting: true,
            }),
            width: '30%',
        },
        {
            ...columnHelper.accessor('asset_type', {
                cell: (info) => {
                    const asset = info.row.original
                    return (
                        <Cell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid="domains-table-classification"
                        >
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                testId={`classification-${asset.asset_type}`}
                            >
                                {DOMAIN_CLASSIFICATION_LABELS[
                                    asset.asset_type
                                ] ?? asset.asset_type}
                            </Typography>
                        </Cell>
                    )
                },
                header: () => (
                    <Typography variant="s-regular">Classification</Typography>
                ),
                enableSorting: true,
            }),
            width: '25%',
        },
        {
            id: 'openIssues',
            ...columnHelper.accessor('issues', {
                cell: (info) => {
                    const asset = info.row.original
                    const issuesCount = countIssuesBySeverity(asset.issues)

                    const severityLevels: Array<
                        Exclude<TIssueSeverity, 'clean'>
                    > = ['critical', 'important', 'moderate']

                    return (
                        <IssuesCell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid="domains-table-open-issues"
                        >
                            {severityLevels.map((severity) => (
                                <Show
                                    key={severity}
                                    when={issuesCount?.[severity]}
                                >
                                    <IssueCountItem
                                        className={`count ${severity}`}
                                        data-testid="domain-issues-severity-count"
                                    >
                                        <SeverityIcon
                                            severity={severity}
                                            dataTestId={`severity-${severity}`}
                                        />
                                        <Typography
                                            className="table-cell-text"
                                            variant="s-regular"
                                        >
                                            {issuesCount?.[severity]}
                                        </Typography>
                                    </IssueCountItem>
                                </Show>
                            ))}
                        </IssuesCell>
                    )
                },
                header: () => (
                    <Typography variant="s-regular">Open Issues</Typography>
                ),
                enableSorting: true,
            }),
            width: '25%',
        },
    ]

    return (
        <StyledCustomTable
            activeRow={activeAssetId}
            getRowId={(row: TAsset) => row.id}
            tableHeight={700}
            columns={columns}
            data={assets}
            sorting={sorting}
            setSorting={setSorting}
            emptyState={<TableEmpty message={emptyAssetsMessage} />}
        />
    )
}

export default DomainsTable
